/**
 * Creating and editing a saved line (a product or a service).
 *
 * This was an inline form that unfolded above the list and pushed every row
 * down by five rows' worth of height, so the thing you were editing left the
 * screen the moment you started editing it. A dialog keeps the list where it
 * was and gives the fields room to be labelled instead of guessed at from
 * placeholders.
 */
import { useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { showVatOn } from "@wa/core/sales-vat";
import { useT } from "../../../i18n/I18nProvider";
import { createItem, updateItem, type Item } from "../../../lib/sales";
import { useInvoicingVat } from "../../useInvoicingVat";
import { Btn, Modal, useToast } from "../../ui";
import { centsToInput, inputToCents } from "./shared";

/** A label over a field, the way the design writes forms. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <label className="crm-lab">{label}</label>
      {children}
    </>
  );
}

/** One sunken pill holding a plain text input. */
function Pill({
  value,
  onChange,
  placeholder,
  inputMode,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputMode?: "numeric" | "decimal";
}) {
  return (
    <label className="crm-inp">
      <input
        type="text"
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
      />
    </label>
  );
}

export function ItemEditModal({
  mode,
  item,
  onClose,
  onSaved,
}: {
  /** "create" also covers duplicating: same blank record, prefilled from `item`. */
  mode: "create" | "edit";
  item?: Item | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const toast = useToast();

  const [name, setName] = useState(item?.name ?? "");
  const [sku, setSku] = useState(item?.sku ?? "");
  const [group, setGroup] = useState(item?.group ?? "");
  const [unit, setUnit] = useState(item?.unit ?? "tk");
  const [price, setPrice] = useState(centsToInput(item?.unitPriceCents ?? 0));
  const { vatEnabled, defaultTaxRatePct } = useInvoicingVat();
  // The template being edited decides, not the switch alone: an item saved back
  // when the company charged VAT still has a rate on it, and hiding the field
  // would leave that rate on the template with no way to clear it.
  const showVat = showVatOn({ vatEnabled, lines: item ? [item] : [] });
  const [tax, setTax] = useState(item ? String(item.taxRatePct) : "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [busy, setBusy] = useState(false);

  /**
   * An optional field that was emptied.
   *
   * The endpoint types every optional field as a string, so it cannot take null
   * and there is no way to write "this field is now empty" on a create. On an
   * edit the empty string is the only way to clear a SKU that was typed by
   * mistake, so that is what an emptied field sends there; on a create the key
   * is left out entirely and the column stays null.
   */
  const optional = (v: string) => (v.trim() ? v.trim() : mode === "edit" ? "" : undefined);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        description: optional(description),
        unit: optional(unit),
        unitPriceCents: inputToCents(price),
        // Clamped rather than sent as typed: the endpoint rejects anything
        // outside 0-100 with a 400 the form has nowhere to show. An untouched
        // field sends nothing at all and lets the server apply the invoicing
        // default, which is zero while the company charges no VAT.
        taxRatePct: tax.trim() ? Math.min(100, Math.max(0, Number.parseInt(tax, 10) || 0)) : undefined,
        sku: optional(sku),
        group: optional(group),
      };
      if (mode === "edit" && item) await updateItem(item.id, body);
      else await createItem(body);
      toast(t("common.saved"));
      onSaved();
      onClose();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  const title = t(mode === "edit" ? "sales.items.edit" : "sales.items.add");

  return (
    <Modal title={title} label={title} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label={t("sales.items.name")}>
          <Pill value={name} onChange={setName} placeholder={t("sales.line.descPlaceholder")} />
        </Field>

        {/* Mobile-first, like the document editor's own field grid: one column is
            what a 375px dialog has room for, and the pair comes back at `sm`. */}
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Field label={t("sales.items.sku")}>
              <Pill value={sku} onChange={setSku} placeholder={t("sales.items.skuPlaceholder")} />
            </Field>
          </div>
          <div>
            <Field label={t("sales.items.group")}>
              <Pill value={group} onChange={setGroup} placeholder={t("sales.items.groupPlaceholder")} />
            </Field>
          </div>
        </div>

        {/* Two on a phone, three from `sm`. Three 106px columns left 70px inside a
            pill for a price, which is not enough for four figures and a comma. */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div>
            <Field label={t("sales.items.unit")}>
              <Pill value={unit} onChange={setUnit} placeholder={t("sales.line.unitPlaceholder")} />
            </Field>
          </div>
          <div>
            <Field label={t("sales.items.price")}>
              <Pill value={price} onChange={setPrice} placeholder="0.00" inputMode="decimal" />
            </Field>
          </div>
          {showVat ? (
            <div>
              <Field label={t("sales.line.tax")}>
                <Pill
                  value={tax}
                  onChange={setTax}
                  placeholder={String(defaultTaxRatePct)}
                  inputMode="numeric"
                />
              </Field>
            </div>
          ) : null}
        </div>

        {/* The description is what lands on the document when the line is picked,
            so it is worth more room than the one-line name field above it. */}
        <Field label={t("sales.line.description")}>
          <textarea
            className="crm-area"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("sales.items.descPlaceholder")}
          />
        </Field>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !name.trim()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("common.save")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
